import React, { useEffect, useRef, useState } from "react";
import {
  Plus, Pencil, Trash2, FileText, Video, Send, CheckCircle2, XCircle, Clock,
  GripVertical, ArrowUp, ArrowDown, Save, ListChecks, FolderKanban, Rocket,
  Paperclip, Upload, Eye, Download as DownloadIcon, Loader2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useRefs } from "@/lib/useRefs";
import api, { apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState, TypeBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = ["sop", "faq", "workflow", "business_rule", "troubleshooting"];
const STATUS_STYLE = {
  draft:     "bg-muted text-muted-foreground border-border",
  submitted: "bg-[#fdf2c9] text-[#7a5a12] border-[#f0d98c]",
  published: "bg-[#d9f2e5] text-[#25604d] border-[#a5dbc0]",
  rejected:  "bg-[#fbe0d9] text-[#a23a2a] border-[#f0b8a8]",
};
const emptyDoc = { program_id: "", type: "sop", title: "", content: "", source: "", tags: [], role_categories: [], role_levels: [], videos: [], attachments: [] };
const MAX_MB = 20;

function humanSize(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${i === 0 ? n.toFixed(0) : n.toFixed(1)} ${u[i]}`;
}

function attachmentUrl(docId, aid, { inline = true } = {}) {
  const tok = localStorage.getItem("kp_token") || "";
  const q = new URLSearchParams({ auth: tok, inline: inline ? "true" : "false" });
  return `${API}/documents/${docId}/attachments/${aid}/download?${q.toString()}`;
}

function DocEditor({ open, onClose, refs, activeProgram, editing, onSaved }) {
  const [form, setForm] = useState(emptyDoc);
  const [tagInput, setTagInput] = useState("");
  const [docId, setDocId] = useState(null);      // document id once created/edited
  const [uploading, setUploading] = useState(null); // {name, size, progress}
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      const base = editing ? { ...emptyDoc, ...editing } : { ...emptyDoc, program_id: activeProgram };
      setForm(base);
      setTagInput((base.tags || []).join(", "));
      setDocId(editing?.id || null);
      setUploading(null);
    }
  }, [open, editing, activeProgram]);

  const toggle = (field, id) => setForm((f) => ({ ...f, [field]: f[field].includes(id) ? f[field].filter((x) => x !== id) : [...f[field], id] }));
  const addVideo = () => setForm((f) => ({ ...f, videos: [...f.videos, { title: "", url: "", duration: "", transcript: "" }] }));
  const updVideo = (i, k, v) => setForm((f) => ({ ...f, videos: f.videos.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)) }));
  const rmVideo = (i) => setForm((f) => ({ ...f, videos: f.videos.filter((_, idx) => idx !== i) }));

  // ---- attachments ----
  const ensureSavedDraft = async () => {
    if (docId) return docId;
    if (!form.title.trim()) {
      toast.error("Please enter a title before uploading an attachment");
      return null;
    }
    setSaving(true);
    try {
      const payload = { ...form, program_id: activeProgram, tags: tagInput.split(",").map((t) => t.trim()).filter(Boolean) };
      const { data } = await api.post("/documents", payload);
      setDocId(data.id);
      setForm((f) => ({ ...f, attachments: data.attachments || [] }));
      toast.success("Draft saved — you can now upload attachments");
      return data.id;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name) || file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed"); return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_MB} MB`); return;
    }
    const id = await ensureSavedDraft();
    if (!id) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading({ name: file.name, size: file.size, progress: 0 });
    try {
      const { data } = await api.post(`/documents/${id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploading((u) => u && { ...u, progress: Math.round((evt.loaded / evt.total) * 100) });
        },
      });
      setForm((f) => ({ ...f, attachments: [...(f.attachments || []), data] }));
      toast.success("Attachment uploaded");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAttachment = async (aid) => {
    if (!docId) return;
    try {
      await api.delete(`/documents/${docId}/attachments/${aid}`);
      setForm((f) => ({ ...f, attachments: (f.attachments || []).filter((a) => a.id !== aid) }));
      toast.success("Attachment removed");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const save = async () => {
    const payload = { ...form, program_id: activeProgram, tags: tagInput.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (docId) await api.put(`/documents/${docId}`, payload);
      else await api.post("/documents", payload);
      toast.success(docId ? "Document updated" : "Document created as draft");
      onClose(); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Document" : "New Document"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Title / Topic</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="doc-title" /></div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Source reference</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1" placeholder="e.g. SOP-ZAP-001" /></div>
          <div><Label>Written content</Label><Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={6} className="mt-1" data-testid="doc-content" /></div>
          <div><Label>Tags (comma separated)</Label><Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} className="mt-1" /></div>

          {/* Videos */}
          <div className="rounded-xl border border-border p-4 bg-background/40">
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                <Video className="w-3.5 h-3.5" /> Videos & transcripts
              </Label>
              <Button size="sm" variant="outline" onClick={addVideo} data-testid="add-video" className="rounded-full">
                <Plus className="w-4 h-4 mr-1" /> Add video
              </Button>
            </div>
            {form.videos.length === 0 && <p className="text-xs text-muted-foreground italic">No videos. Some documents have several, others none.</p>}
            <div className="space-y-3">
              {form.videos.map((v, i) => (
                <div key={i} className="rounded-lg bg-card border border-border p-3 space-y-2" data-testid={`video-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input value={v.title} onChange={(e) => updVideo(i, "title", e.target.value)} placeholder="Video title" className="flex-1" />
                    <Input value={v.duration} onChange={(e) => updVideo(i, "duration", e.target.value)} placeholder="6:12" className="w-20" />
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => rmVideo(i)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  <Input value={v.url} onChange={(e) => updVideo(i, "url", e.target.value)} placeholder="Video URL" />
                  <Textarea value={v.transcript} onChange={(e) => updVideo(i, "transcript", e.target.value)} placeholder="Transcript (used by the AI assistant & search)" rows={2} />
                </div>
              ))}
            </div>
          </div>

          {/* Attachments (PDFs) */}
          <div
            className="rounded-xl border border-border p-4 bg-background/40"
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={onDrop}
          >
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                <Paperclip className="w-3.5 h-3.5" /> Attachments · PDF up to {MAX_MB} MB
              </Label>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                data-testid="attachment-file-input"
                onChange={(e) => uploadFile(e.target.files?.[0])}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={!!uploading || saving}
                data-testid="add-attachment"
                className="rounded-full">
                {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving draft…</>
                        : <><Upload className="w-4 h-4 mr-1" /> Upload PDF</>}
              </Button>
            </div>

            {(!form.attachments || form.attachments.length === 0) && !uploading && (
              <p className="text-xs text-muted-foreground italic">
                Drop a PDF here or click Upload. Every doc can carry any mix of attachments, videos and text.
              </p>
            )}

            {uploading && (
              <div className="rounded-lg bg-card border border-border p-3 mb-2" data-testid="attachment-uploading">
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  <span className="truncate flex-1">{uploading.name}</span>
                  <span className="text-[11px] font-mono text-muted-foreground">{humanSize(uploading.size)} · {uploading.progress}%</span>
                </div>
                <Progress value={uploading.progress} className="h-1.5 mt-2" />
              </div>
            )}

            <div className="space-y-2">
              {(form.attachments || []).map((a) => (
                <div key={a.id} className="rounded-lg bg-card border border-border p-3 flex items-center gap-3" data-testid={`attachment-${a.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-secondary/60 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground truncate">{a.title || a.filename}</p>
                    <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                      {a.filename} · {humanSize(a.size)}
                    </p>
                  </div>
                  <a href={docId ? attachmentUrl(docId, a.id, { inline: true }) : "#"} target="_blank" rel="noreferrer"
                     className="text-xs font-semibold text-primary flex items-center gap-1 kp-link">
                    <Eye className="w-3.5 h-3.5" /> View
                  </a>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive"
                          onClick={() => removeAttachment(a.id)}
                          data-testid={`remove-attachment-${a.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Access — Role Categories <span className="text-xs text-muted-foreground font-normal">(empty = all)</span></Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {refs.roleCategories.map((r) => (
                <button key={r.id} onClick={() => toggle("role_categories", r.id)}
                  className={`text-xs rounded-full border px-3 py-1.5 transition-all ${form.role_categories.includes(r.id) ? "border-primary bg-secondary/60 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}>{r.name}</button>
              ))}
            </div>
          </div>
          <div>
            <Label>Access — Role Levels <span className="text-xs text-muted-foreground font-normal">(empty = all)</span></Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {refs.roleLevels.map((r) => (
                <button key={r.id} onClick={() => toggle("role_levels", r.id)}
                  className={`text-xs rounded-full border px-3 py-1.5 transition-all ${form.role_levels.includes(r.id) ? "border-primary bg-secondary/60 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}>{r.name.split(" ")[0]}</button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="doc-save" className="rounded-full">{docId ? "Save changes" : "Save Draft"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocumentsTab({ refs, activeProgram }) {
  const [docs, setDocs] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => activeProgram && api.get("/documents", { params: { program: activeProgram } }).then((r) => setDocs(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [activeProgram]);

  const act = async (id, action) => {
    try {
      await api.post(`/documents/${id}/${action}`);
      toast.success(`Document ${action === "submit" ? "submitted for approval" : action}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground">{docs.length} documents in this program</p>
        <Button onClick={() => { setEditing(null); setEditorOpen(true); }} data-testid="new-document" className="rounded-full">
          <Plus className="w-4 h-4 mr-1.5" /> New Document
        </Button>
      </div>

      {docs.length === 0 ? <EmptyState icon={FileText} title="No documents yet" hint="Create your first knowledge document." /> : (
        <div className="space-y-3 stagger" data-testid="documents-list">
          {docs.map((d) => (
            <div key={d.id} className="kp-card kp-card-lift p-5" data-testid={`doc-${d.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <TypeBadge type={d.type} />
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg text-foreground leading-tight">{d.title}</p>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-3 flex-wrap mt-1.5">
                    <span>{d.source}</span>
                    {d.videos?.length > 0 && <span className="flex items-center gap-1"><Video className="w-3 h-3" /> {d.videos.length} video{d.videos.length > 1 ? "s" : ""}</span>}
                    {d.attachments?.length > 0 && <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" /> {d.attachments.length} PDF{d.attachments.length > 1 ? "s" : ""}</span>}
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {d.last_updated}</span>
                  </p>
                </div>
                <Badge variant="outline" className={`capitalize font-medium ${STATUS_STYLE[d.status]}`} data-testid={`doc-status-${d.id}`}>{d.status}</Badge>
              </div>

              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border flex-wrap">
                {(d.status === "submitted" || d.status === "published") && (
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className={`flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${d.pm_approved ? "bg-[#d9f2e5] text-[#25604d] border-[#a5dbc0]" : "bg-muted text-muted-foreground border-border"}`}>
                      {d.pm_approved ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />} PM
                    </span>
                    <span className={`flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${d.admin_approved ? "bg-[#d9f2e5] text-[#25604d] border-[#a5dbc0]" : "bg-muted text-muted-foreground border-border"}`}>
                      {d.admin_approved ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />} Admin
                    </span>
                  </div>
                )}
                {d.reject_comment && d.status === "rejected" && <span className="text-xs text-destructive italic">Rejected: {d.reject_comment}</span>}

                <div className="ml-auto flex items-center gap-1">
                  {(d.status === "draft" || d.status === "rejected") && (
                    <Button size="sm" className="rounded-full" onClick={() => act(d.id, "submit")} data-testid={`submit-${d.id}`}>
                      <Send className="w-3.5 h-3.5 mr-1" /> Submit
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" onClick={() => { setEditing(d); setEditorOpen(true); }} data-testid={`edit-doc-${d.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(d.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <DocEditor open={editorOpen} onClose={() => setEditorOpen(false)} refs={refs} activeProgram={activeProgram} editing={editing} onSaved={load} />
    </div>
  );
}

function ChecklistTab({ activeProgram }) {
  const [steps, setSteps] = useState([]);

  const load = () => activeProgram && api.get(`/programs/${activeProgram}/checklist`).then((r) => setSteps(r.data.steps)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [activeProgram]);

  const upd = (i, patch) => setSteps((s) => s.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const move = (i, dir) => setSteps((s) => {
    const j = i + dir; if (j < 0 || j >= s.length) return s;
    const copy = [...s]; [copy[i], copy[j]] = [copy[j], copy[i]]; return copy;
  });
  const add = () => setSteps((s) => [...s, { name: "New step", mandatory: true, status: "missing" }]);
  const rm = (i) => setSteps((s) => s.filter((_, idx) => idx !== i));

  const save = async () => {
    const { data } = await api.put(`/programs/${activeProgram}/checklist`, { steps });
    setSteps(data.steps);
    toast.success("Checklist saved");
  };

  const doneCount = steps.filter((s) => s.status === "done").length;
  const mandDone = steps.filter((s) => s.mandatory && s.status === "done").length;
  const mandTotal = steps.filter((s) => s.mandatory).length;
  const ready = mandTotal === 0 || mandDone === mandTotal;

  return (
    <div>
      <div className="kp-card p-5 mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground">Publishing readiness</p>
          <p className="font-display text-lg text-foreground mt-0.5">{mandDone}/{mandTotal} mandatory · {doneCount}/{steps.length} total</p>
          <Progress value={mandTotal ? (mandDone / mandTotal) * 100 : 100} className="mt-3 h-2 w-64" />
        </div>
        <Badge variant="outline" className={`font-medium ${ready ? "bg-[#d9f2e5] text-[#25604d] border-[#a5dbc0]" : "bg-[#fbe0d9] text-[#a23a2a] border-[#f0b8a8]"}`} data-testid="checklist-ready">
          {ready ? <><Rocket className="w-3 h-3 mr-1" /> Ready to Publish</> : <>Blocked — {mandTotal - mandDone} mandatory missing</>}
        </Badge>
      </div>

      <div className="kp-card divide-y divide-border overflow-hidden" data-testid="checklist-steps">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-3 p-3.5 hover:bg-secondary/20 transition" data-testid={`checklist-step-${i}`}>
            <GripVertical className="w-4 h-4 text-muted-foreground/40 shrink-0" />
            <span className="w-6 h-6 rounded-md bg-secondary/60 text-primary text-xs font-mono flex items-center justify-center shrink-0">{i + 1}</span>
            <Input value={s.name} onChange={(e) => upd(i, { name: e.target.value })} className="flex-1 h-8 bg-background" data-testid={`step-name-${i}`} />
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
              <Switch checked={s.mandatory} onCheckedChange={(v) => upd(i, { mandatory: v })} /> Mandatory
            </label>
            <button onClick={() => upd(i, { status: s.status === "done" ? "missing" : "done" })} data-testid={`step-toggle-${i}`}
              className={`flex items-center gap-1.5 text-xs font-medium rounded-full border px-3 py-1.5 transition shrink-0 ${
                s.status === "done" ? "bg-[#d9f2e5] text-[#25604d] border-[#a5dbc0]" : "bg-[#fbe0d9] text-[#a23a2a] border-[#f0b8a8]"}`}>
              {s.status === "done" ? <><CheckCircle2 className="w-3.5 h-3.5" /> Done</> : <><XCircle className="w-3.5 h-3.5" /> Missing</>}
            </button>
            <div className="flex shrink-0">
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, -1)}><ArrowUp className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, 1)}><ArrowDown className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => rm(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-4">
        <Button variant="outline" onClick={add} data-testid="add-step" className="rounded-full"><Plus className="w-4 h-4 mr-1.5" /> Add Step</Button>
        <Button onClick={save} data-testid="save-checklist" className="rounded-full"><Save className="w-4 h-4 mr-1.5" /> Save Checklist</Button>
      </div>
    </div>
  );
}

export default function ContentManagement() {
  const { user } = useAuth();
  const refs = useRefs();
  const [tab, setTab] = useState("documents");
  const [pid, setPid] = useState("");

  const managed = refs.managedPrograms;
  const isManager = user?.is_admin || user?.role_category === "rc_approver";

  useEffect(() => {
    if (!managed.length) return;
    setPid((cur) => (managed.some((p) => p.id === cur) ? cur : managed[0].id));
  }, [managed]);

  if (!isManager)
    return <EmptyState icon={FolderKanban} title="Managers only" hint="Content management is available to administrators and program managers." />;

  const program = managed.find((p) => p.id === pid);

  return (
    <div>
      <PageHeader
        eyebrow="Console · Content Management"
        title="Content Management"
        subtitle="Author knowledge per program. Nothing reaches users or the AI assistant until it is approved and published."
        testid="content-header">
        <div className="flex items-center gap-2" data-testid="content-program-switcher">
          <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground hidden sm:block">Program</span>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger className="w-60 rounded-full bg-card" data-testid="content-program-select"><SelectValue placeholder="Select program" /></SelectTrigger>
            <SelectContent>
              {managed.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`content-program-option-${p.id}`}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </PageHeader>

      {managed.length === 0 ? (
        <EmptyState icon={FolderKanban} title="No programs to manage" hint="An administrator needs to make you the manager of a program." />
      ) : (
        <>
          <div className="kp-card px-5 py-3.5 mb-6 flex items-center gap-3 flex-wrap">
            <div className="w-9 h-9 rounded-xl bg-secondary/60 flex items-center justify-center shrink-0">
              <FolderKanban className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">{program?.name}</p>
              <p className="text-[10px] font-mono uppercase tracking-wider text-primary">{program?.jira_tag} · PM {program?.pm_name}</p>
            </div>
          </div>

          <div className="flex gap-1 mb-6 border-b border-border">
            {[{ id: "documents", label: "Documents", icon: FileText }, { id: "checklist", label: "Content Checklist", icon: ListChecks }].map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`content-tab-${t.id}`}
                className={`flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-t-lg border-b-2 -mb-[2px] transition-all ${
                  tab === t.id
                    ? "border-primary text-primary bg-secondary/30"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/20"}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "documents"
            ? <DocumentsTab key={pid} refs={refs} activeProgram={pid} />
            : <ChecklistTab key={pid} activeProgram={pid} />}
        </>
      )}
    </div>
  );
}
